"use client"

import WatsonApi from "../../../backends/WatsonApi";
import {useStores} from "../../../stores/useStores";
import {Dispatch, SetStateAction, useEffect, useOptimistic, useRef, useState, useTransition} from "react";
import {
    Box, Button,
    Flex, Grid, Heading,
    IconButton,
    Separator, TextArea
} from "@radix-ui/themes";
import FontAwesome from "../../utilities/FontAwesome";
import {IChannel} from "./ChannelsList";
import useSWRInfinite from "swr/infinite";
import {Message} from "./Message";
import {IMessage, IMessageAttachment} from "../../modals/ActivityModal/ActivityModal";
import {flushSync} from "react-dom";
import styles from "../styles/Conversation.module.scss";
import NotificationManager from "../../notifications/NotificationManager";
import {FileUploader} from "./FileUploader";
import {AttachmentPreview} from "./AttachmentPreview";
import {MessagingListRulesQuery} from "../../modals/Messaging/MessagingListRules";
import {RecipientRuleBadge, RecipientUserBadge} from "./RecipientBadge";
import {SaveListData} from "../../modals/Messaging/AddOrEditMessagingListModal";

export type IPendingMessageAttachment = IMessageAttachment & {
    save_url: string,
}

let fetchNewMessagesTimeout: NodeJS.Timeout | undefined;
const fetchNewMessagesIntervalMs = 5000;

export default function Conversation(props: {
    recipients: IUser[],
    setRecipientUsers: Dispatch<SetStateAction<IUser[]>>,
    recipientRules: MessagingListRulesQuery[],
    setRecipientRules: Dispatch<SetStateAction<MessagingListRulesQuery[]>>,
    currentChannel: IChannel | undefined,
    setCurrentChannel: (id: IChannel | undefined) => void,
    allowReplies: boolean,
    setShowConversation: Dispatch<SetStateAction<boolean>>,
    mutateChannels: any,
}) {
    const oldMessagesThreshold = useRef<string>(new Date().toISOString());
    const textAreaRef = useRef<HTMLTextAreaElement>(null);

    const {organizationStore, userStore, modalStore} = useStores();
    const [inputText, setInputText] = useState("");
    const [newMessageListType, setNewMessageListType] = useState<"message" | "broadcast" | null>(props.currentChannel?.type || null);
    const [attachments, setAttachments] = useState<IPendingMessageAttachment[]>([]);
    const [unreadMessages, setUnreadMessages] = useState<IMessage[]>([]);
    const [optimisticUnreadMessages, setOptimisticUnreadMessages] = useOptimistic<IMessage[], IMessage>(unreadMessages, (state, optimisticMessage) => {
        return [
            ...state,
            optimisticMessage,
        ]
    });
    const [isPending, startTransition] = useTransition();

    function addAttachment(attachment: IPendingMessageAttachment) {
        setAttachments(prev => [...prev, attachment]);
    }

    function deleteAttachment(url: IPendingMessageAttachment["url"]) {
        setAttachments(prev => prev.filter(a => a.url !== url));
    }

    const {
        data: oldData,
        isLoading: isLoadingOldData,
        setSize: setOldDataSize,
        isValidating: isValidatingOldData
    } = useSWRInfinite((pageIndex) => props.currentChannel ? (JSON.stringify({
        page: pageIndex,
        key: props.currentChannel.id + "messages-list",
    })) : null, async (key) => {
        const parsedKey = JSON.parse(key);
        const client = await WatsonApi();
        const nextPage = parsedKey.page + 1;
        const response = await client.apis.message_list.message_list_messages_list({
            message_list_pk: (props.currentChannel as IChannel).id,
            page: nextPage,
            page_size: 12,
            created_at__lte: oldMessagesThreshold.current,
            ordering: "-message__created_at",
        });
        const result = JSON.parse(response.data);
        return {
            messages: result.data.reverse(),
            total: result.meta.total,
        }
    }, {
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
        revalidateIfStale: true,
        revalidateOnMount: true,
    })

    const oldMessages: IMessage[] = oldData?.reduce((acc, curr) => [...curr.messages, ...acc], []) || [];
    const totalOldMessages: number = oldData?.[0].total || 0;
    const isMoreOldMessages = !isLoadingOldData && totalOldMessages > oldMessages.length;

    async function fetchNewMessages() {
        clearTimeout(fetchNewMessagesTimeout);

        const client = await WatsonApi();
        const response = await client.apis.message_list.message_list_messages_list({
            message_list_pk: (props.currentChannel as IChannel).id,
            created_at__gt: oldMessagesThreshold.current,
            read_at__isnull: true,
        });
        const newMessages = JSON.parse(response.data).data;
        if (newMessages.length > 0) {
            startTransition(() => {
                setUnreadMessages(v => [...v, ...newMessages]);
            })

            await client.apis.message_list.message_list_messages_mark_read({
                message_list_pk: (props.currentChannel as IChannel).id,
                created_at__lte: newMessages[newMessages.length - 1].created_at,
            })
        }

        fetchNewMessagesTimeout = setTimeout(fetchNewMessages, fetchNewMessagesIntervalMs);
    }

    useEffect(() => {
        if (props.currentChannel?.id) {
            fetchNewMessagesTimeout = setTimeout(fetchNewMessages, fetchNewMessagesIntervalMs);
        }
        return () => clearTimeout(fetchNewMessagesTimeout);
    }, [props.currentChannel?.id]);

    const messages: IMessage[] = [...oldMessages, ...optimisticUnreadMessages];

    const handleSend = async () => {
        const inputValue = inputText;

        const q = {
            content: inputValue,
            type: newMessageListType,
            attachments: attachments.map(attachment => ({
                content_type: attachment.content_type,
                url: attachment.save_url,
                name: attachment.name,
            })),
        }
        if (props.currentChannel) {
            q["message_list_id"] = props.currentChannel.id;
        } else {
            if (props.recipients.length > 0) q["recipient_ids"] = props.recipients.map(user => user.id);
            if (props.recipientRules.length > 0) q["rules"] = props.recipientRules;
        }

        if (!q["recipient_ids"] && !q["rules"] && !q["message_list_id"]) {
            return;
        }

        setOptimisticUnreadMessages({
            id: -1,
            content: `<p>${inputValue}</p>`,
            type: "message",
            branding: userStore.avatar ? {
                type: "image",
                value: userStore.avatar,
                alt: "user avatar",
            } : {
                type: "text",
                value: `${userStore.firstName[0]}${userStore.lastName[0]}`,
            },
            created_at: new Date().toISOString(),
            title: `${userStore.firstName} ${userStore.lastName}`,
            grouping_id: "",
            actionable_link: null,
            attachments,
        })

        flushSync(() => {
            // flush this synchronously so optimistic message shows up immediately
            setInputText("");
            setAttachments([]);
        })

        try {
            const client = await WatsonApi();

            const response = await client.apis.organizations.organizations_messaging_create({
                organization_pk: organizationStore.organization.id,
                data: q
            });

            if (props.currentChannel) {
                await fetchNewMessages();
            } else {
                const messageListId = JSON.parse(response.data).message_list_id;
                const [channels] = await props.mutateChannels();
                startTransition(async () => {
                    props.setCurrentChannel(channels.find(channel => channel.id === messageListId));
                    setAttachments([]);
                    await fetchNewMessages();
                })
            }
        } catch (e) {
            console.error(e);
            NotificationManager.error("Failed to send message", undefined, 3000);
            setUnreadMessages(v => v);
        }
    }

    async function handleLoadOlder() {
        await setOldDataSize(s => s + 1);
    }

    let headingContent;
    if (props.currentChannel) {
        headingContent = <Heading as={"h3"} size={"3"}>{props.currentChannel.title}</Heading>;
    } else if (props.recipients.length > 0 || props.recipientRules.length > 0) {
        headingContent = <Heading as={"h3"} size={"3"}>
            To: {props.recipientRules.map(rule => <RecipientRuleBadge
            rule={rule}
            removeRule={() => props.setRecipientRules(prev => prev.filter(r => JSON.stringify(r) !== JSON.stringify(rule)))}
        />)} {props.recipients.map(user => <RecipientUserBadge
            user={user}
            removeUser={() => props.setRecipientUsers(prev => prev.filter(u => u.id !== user.id))}
        />)}
        </Heading>
    } else if (props.recipients.length === 0 && props.recipientRules.length === 0) {
        headingContent = <Flex asChild align={"center"}>
            <Heading as={"h3"} size={"3"}>
                To:&nbsp;<Button size={"2"} variant={"soft"} onClick={() => {
                modalStore.addModal({
                    type: "AddOrEditMessagingListModal",
                    handleSave: async (listData: SaveListData) => {
                        props.setRecipientUsers(listData.members);
                        props.setRecipientRules(listData.rules);
                        setNewMessageListType(listData.type);
                        props.setCurrentChannel(undefined);
                        modalStore.removeTop();
                    },
                    modalIntent: "createMessage",
                    createMessageIntent: "broadcast",
                })
            }}>
                Add Recipients
            </Button>
            </Heading>
        </Flex>
    }

    function handleTextChange(e) {
        setInputText(e.target.value);
        if (textAreaRef.current) {
            textAreaRef.current.style.height = "auto";
            const newScrollHeight = Math.min(textAreaRef.current.scrollHeight, 200);
            textAreaRef.current.style.height = newScrollHeight + "px";
        }
    }

    const isDisabled = !inputText || (
        // no recipients and not sending to an existing list
        props.recipients.length === 0 && props.recipientRules.length === 0 && !props.currentChannel?.id
    ) || (
        // current user is only recipient and not sending to an existing list
        props.recipients.length === 1 && props.recipients[0].id === userStore.id && props.recipientRules.length === 0
    );

    return <Box
        flexBasis={{
            initial: "100%",
            md: "50%",
        }}
        height={"500px"}
        style={{boxShadow: "var(--shadow-6)", backgroundColor: "var(--gray-2)"}}
    >
        <Grid rows={"3"} height={"100%"} style={{gridTemplateRows: "auto 1fr auto"}}>
            <Flex p={"2"} justify={"between"} align={"center"} style={{borderBottom: "1px solid var(--gray-6"}}>
                <Box>
                    {headingContent}
                </Box>
                <IconButton variant={"ghost"} onClick={() => {
                    props.setShowConversation(false);
                    props.setRecipientUsers([]);
                    props.setRecipientRules([]);
                    props.setCurrentChannel(undefined);
                }}>
                    <FontAwesome name={"fa-times"} prefix={"fas"}/>
                </IconButton>
            </Flex>
            <Box px={"2"} className={styles.messagesContainer}>
                <div className={styles.spaceConsumer} />
                {messages.reverse().map(msg => <Message key={msg.id} message={msg}/>)}
                {isMoreOldMessages && <Box pt={"3"} style={{textAlign: "center"}}>
                    <Button variant={"ghost"} onClick={handleLoadOlder} loading={isValidatingOldData}>
                        Load Older
                    </Button>
                    <Separator my={"3"} mx={"auto"} orientation={"horizontal"} size={"2"}/>
                </Box>}
            </Box>
            {props.allowReplies && <Box p={"2"} pt={"0"}>
                <Separator my={"3"} orientation={"horizontal"} size={"4"}/>
                <Flex gap={"3"} align={"center"}>
                    <FileUploader addAttachment={addAttachment}/>
                    <Box width={"100%"}>
                        <Flex align={"center"} gapX={"2"} asChild>
                            <form action={handleSend}>
                                <TextArea
                                    ref={textAreaRef}
                                    onChange={handleTextChange}
                                    className={styles.textInput}
                                    size={"3"}
                                    rows={1}
                                    wrap={"soft"}
                                    placeholder={"Type message here"}
                                    value={inputText}
                                />
                                <Button variant={"soft"} disabled={isDisabled}>
                                    Send
                                </Button>
                            </form>
                        </Flex>
                        {attachments.length > 0 && <Box mt={"2"}>
                            <Flex gap={"2"} wrap={"wrap"}>
                                {attachments.map(attachment => <AttachmentPreview
                                    key={attachment.url}
                                    attachment={attachment}
                                    deleteAttachment={deleteAttachment}
                                />)}
                            </Flex>
                        </Box>}
                    </Box>
                </Flex>
            </Box>}
        </Grid>
    </Box>;
}